import { type JSX, type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';

// ---------------------------------------------------------------------------
// WideModal — a lightweight wide modal for hosting content wider than the
// shared ConfirmModal (which caps at max-w-[440px]). Mirrors ConfirmModal's
// overlay conventions (fixed inset overlay, bg-ink/50, backdrop-click + Esc to
// close, stopPropagation on the panel) at a wider max width with an internal
// scroll region. Promoted here from the voice quick wizard so SMS surfaces can
// reuse it without importing from the voice module.
// ---------------------------------------------------------------------------
export function WideModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}): JSX.Element {
  const { t } = useTranslation('default');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wide-modal-title"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[920px] max-h-[85vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-line bg-surface">
          <h2 id="wide-modal-title" className="display text-[16px] font-semibold text-text">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('cancel')}
            className="text-text-4 hover:text-text"
          >
            <Icon.xMark width="18" height="18" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
